'use client';

import * as amplitude from '@amplitude/analytics-browser';
import { Identify as AmplitudeIdentify } from '@amplitude/analytics-browser';
import { z } from 'zod';

import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';

// SCR-5478 Layer 3: the deep-link payload is attacker-influenceable
// (`heidiscribe://` accepts any `dl_attr`), so it is validated here, not trusted
// from the plugin. `.max(256)` drops an over-long crafted id; `.catch(undefined)`
// treats a malformed id as absent.
const downloadAttributionSchema = z.object({
  amp_device_id: z.string().min(1).max(256).optional().catch(undefined),
});

// UTM + click IDs only; names match the existing bare Amplitude props 1:1. Other
// payload keys (platform, variant, feature, click_at, ref, fbc, fbp, gclsrc,
// gad_source, snowplowid, amp_device_id) are intentionally not forwarded.
const ATTRIBUTION_USER_PROPERTY_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'li_fat_id',
  'msclkid',
  'ttclid',
  'twclid',
  'rdt_cid',
] as const;

// Matches the producer cookie's per-value cap (mkt #1255) so no valid value is
// silently dropped.
const MAX_USER_PROPERTY_VALUE_LENGTH = 512;

export interface DownloadAttribution {
  ampDeviceId?: string;
  userProperties: Record<string, string>;
}

function extractUserProperties(record: Record<string, unknown>): Record<string, string> {
  const properties: Record<string, string> = {};

  for (const key of ATTRIBUTION_USER_PROPERTY_KEYS) {
    const value = record[key];
    if (typeof value !== 'string') continue;
    if (value.length === 0 || value.length > MAX_USER_PROPERTY_VALUE_LENGTH) continue;

    properties[key] = value;
  }

  return properties;
}

export function parseDownloadAttribution(raw: string): DownloadAttribution | null {
  try {
    const json: unknown = JSON.parse(raw);
    if (typeof json !== 'object' || json === null || Array.isArray(json)) {
      return null;
    }

    const record = json as Record<string, unknown>;
    const parsed = downloadAttributionSchema.safeParse(record);

    return {
      ampDeviceId: parsed.success ? parsed.data.amp_device_id : undefined,
      userProperties: extractUserProperties(record),
    };
  } catch {
    return null;
  }
}

/** Best-effort — a missing/malformed payload or SDK error must never block login. */
export function attributeDownloadOnSignup(raw: string): void {
  // TELEMETRY-GATE: F1 — no Amplitude egress on a suppressed build.
  if (!isSaasTelemetryEnabled) return;

  try {
    const attribution = parseDownloadAttribution(raw);
    if (!attribution) return;

    const { ampDeviceId, userProperties } = attribution;
    const propertyEntries = Object.entries(userProperties);
    if (!ampDeviceId && propertyEntries.length === 0) return;

    // Set the web device id before the identify that carries the user id, so
    // Amplitude merges the anonymous web funnel onto the signed-up user.
    if (ampDeviceId) amplitude.setDeviceId(ampDeviceId);

    // setOnce: first attributed download wins, later logins never overwrite it.
    const identify = new AmplitudeIdentify();
    for (const [key, value] of propertyEntries) {
      identify.setOnce(key, value);
    }
    amplitude.identify(identify);
  } catch (error) {
    logger.warn('[Auth] Failed to attribute download on signup', error);
  }
}
